import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { cancelUploadSession, completeUploadSession, reserveUploadBatch, uploadToPresignedPost } from '../services/documents';

export type DocumentUploadStatus = 'queued' | 'uploading' | 'completing' | 'completed' | 'failed' | 'cancelled';

export interface DocumentUploadItem {
  id: string;
  file: File;
  fileName: string;
  spaceId: number;
  parentId: number | null;
  status: DocumentUploadStatus;
  progress: number;
  uploadId?: string;
  nodeId?: number;
  error?: string;
}

interface DocumentUploadContextValue {
  items: DocumentUploadItem[];
  revisionBySpace: Record<number, number>;
  enqueue: (files: File[], spaceId: number, parentId: number | null) => void;
  retry: (id: string) => void;
  cancel: (id: string) => void;
  dismiss: (id: string) => void;
}

const DocumentUploadContext = createContext<DocumentUploadContextValue | null>(null);
const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function DocumentUploadProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<DocumentUploadItem[]>([]);
  const [revisionBySpace, setRevisionBySpace] = useState<Record<number, number>>({});
  const controllers = useRef(new Map<string, AbortController>());
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const patchItem = useCallback((id: string, patch: Partial<DocumentUploadItem>) => {
    setItems((current) => current.map((item) => item.id === id ? { ...item, ...patch } : item));
  }, []);

  const run = useCallback(async (item: DocumentUploadItem) => {
    const controller = new AbortController();
    controllers.current.set(item.id, controller);
    patchItem(item.id, { status: 'queued', progress: 0, error: undefined, uploadId: undefined });
    let uploadId: string | undefined;
    try {
      const [session] = await reserveUploadBatch({
        spaceId: item.spaceId,
        parentId: item.parentId,
        idempotencyKey: makeId(),
        files: [{ client_id: item.id, name: item.file.name, size_bytes: item.file.size, content_type: item.file.type }],
      });
      uploadId = session.upload_id;
      patchItem(item.id, { status: 'uploading', uploadId });
      await uploadToPresignedPost(session, item.file, {
        signal: controller.signal,
        onProgress: ({ percentage }) => patchItem(item.id, { progress: percentage }),
      });
      patchItem(item.id, { status: 'completing', progress: 100 });
      const node = await completeUploadSession(session.upload_id);
      patchItem(item.id, { status: 'completed', nodeId: node.id, progress: 100 });
      setRevisionBySpace((current) => ({ ...current, [item.spaceId]: (current[item.spaceId] ?? 0) + 1 }));
    } catch (error) {
      if (controller.signal.aborted) {
        if (uploadId) await cancelUploadSession(uploadId).catch(() => undefined);
        patchItem(item.id, { status: 'cancelled', error: undefined });
      } else {
        patchItem(item.id, { status: 'failed', error: 'Caricamento non riuscito. Riprova.' });
      }
    } finally {
      controllers.current.delete(item.id);
    }
  }, [patchItem]);

  const enqueue = useCallback((files: File[], spaceId: number, parentId: number | null) => {
    const queued = files.map((file): DocumentUploadItem => ({
      id: makeId(), file, fileName: file.name, spaceId, parentId, status: 'queued', progress: 0,
    }));
    setItems((current) => [...queued, ...current]);
    queued.forEach(run);
  }, [run]);

  const retry = useCallback((id: string) => {
    const item = itemsRef.current.find((candidate) => candidate.id === id);
    if (item && (item.status === 'failed' || item.status === 'cancelled')) run(item);
  }, [run]);

  const cancel = useCallback((id: string) => {
    const controller = controllers.current.get(id);
    if (controller) controller.abort();
    else patchItem(id, { status: 'cancelled' });
  }, [patchItem]);

  const dismiss = useCallback((id: string) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const value = useMemo(() => ({ items, revisionBySpace, enqueue, retry, cancel, dismiss }), [items, revisionBySpace, enqueue, retry, cancel, dismiss]);
  return <DocumentUploadContext.Provider value={value}>{children}</DocumentUploadContext.Provider>;
}

export function useDocumentUploads() {
  const value = useContext(DocumentUploadContext);
  if (!value) throw new Error('useDocumentUploads must be used within DocumentUploadProvider');
  return value;
}
